import {
    Column,
    Container,
    cssVar,
    Grid,
    Label,
    Row,
    Separator,
    useAlignments,
    useScreen,
    useSizes,
} from 'metrix-common3';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import PolicyListItem from './PolicyListItem';

const TableContainer = styled.div`
    position: relative;
    width: ${(props) => (props.$isDesktop ? '82vw' : '100vw')};
    border: 1px solid ${cssVar.colorStrokeLight};
    border-radius: 4px;
`;

const HeaderWrapper = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: white;
    width: 100%;
`;

const ScrollableWrapper = styled.div`
    overflow-x: auto;
    /* Inset the viewport between the edge buttons so the first/last columns aren't hidden under them */
    margin-left: ${(props) => props.$inset || 0}px;
    margin-right: ${(props) => props.$inset || 0}px;
`;

// Width of the edge scroll buttons; shadows are offset inboard by this amount
// so the opaque buttons don't cover them.
const EDGE_BUTTON_WIDTH = 28;

// Shadow hints rendered just inside each edge button, signalling more content
// when the grid can still scroll that way.
const LeftShadow = styled.div`
    position: absolute;
    top: 0;
    left: ${(props) => props.$offset}px;
    height: 100%;
    width: 32px;
    pointer-events: none;
    background: linear-gradient(to right, rgba(0,0,0,0.3), rgba(0,0,0,0));
    opacity: ${(props) => (props.$visible ? 1 : 0)};
    transition: opacity 0.3s ease;
    z-index: 2;
`;

const RightShadow = styled.div`
    position: absolute;
    top: 0;
    right: ${(props) => props.$offset}px;
    height: 100%;
    width: 32px;
    pointer-events: none;
    background: linear-gradient(to left, rgba(0,0,0,0.3), rgba(0,0,0,0));
    opacity: ${(props) => (props.$visible ? 1 : 0)};
    transition: opacity 0.3s ease;
    z-index: 2;
`;

// Full-height clickable bars on each edge that jump-scroll the grid to the
// far start/end. Disabled (greyed, non-clickable) when there's no scroll that way.
const ScrollButton = styled.button`
    position: absolute;
    top: 0;
    bottom: 0;
    ${(props) => props.$side}: 0;
    width: ${EDGE_BUTTON_WIDTH}px;
    margin: 0;
    padding: 0;
    border: none;
    border-${(props) => props.$side}: 1px solid ${cssVar.colorStrokeLight};
    z-index: 3;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover:not(:disabled) {
        background-color: ${cssVar.colorStrokeVeryLight};
    }

    &:disabled {
        cursor: default;
        background-color: ${cssVar.colorBackgroundLight};
    }
`;

const ScrollChevron = ({ $direction, $disabled }) => (
    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none'>
        <path
            d={$direction === 'left' ? 'M15 18 9 12l6-6' : 'M9 18l6-6-6-6'}
            stroke={$disabled ? cssVar.colorStrokeVeryDark : cssVar.colorPrimary}
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
        />
    </svg>
);

const FixedWidthGrid = styled(Grid)`
    min-width: ${(props) => props.minWidth || '1200px'};
    width: 100%;
`;

const LabelItem = styled(Label)`
    line-height: 1.5rem;
    font-size: .75rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const AZSortIcon = ({ $isActive }) => (
    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' cursor='pointer'>
        <path
            fill={$isActive ? cssVar.colorPrimary : cssVar.colorStrokeVeryDark}
            d='m0 12 3-8h1.72l3 8H6.08l-.68-1.92H2.32L1.68 12H0Zm2.8-3.28h2.08l-.96-3H3.8l-1 3ZM9.36 12v-1.52l4.04-5.04H9.52V4h5.64v1.52l-4 5.04h4.04V12H9.36ZM5.6 2.4 8 0l2.4 2.4H5.6ZM8 16l-2.4-2.4h4.8L8 16Z'
        />
    </svg>
);

const SortIcon = ({ $isActive, isDesc }) => (
    <svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16' cursor='pointer'>
        <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M3 4H1L5 0L9 4H7V9H3V4Z'
            fill={$isActive && !isDesc ? cssVar.colorPrimary : cssVar.colorStrokeVeryDark}
        />
        <path
            fillRule='evenodd'
            clipRule='evenodd'
            d='M13 12L15 12L11 16L7 12L9 12L9 7L13 7L13 12Z'
            fill={$isActive && isDesc ? cssVar.colorPrimary : cssVar.colorStrokeVeryDark}
        />
    </svg>
);

const Results = ({ listPolicies, history, context, query, doReduceQueryElement, doGetListPolicies }) => {
    const intl = useIntl();
    const sizes = useSizes();
    const alignments = useAlignments();
    const screen = useScreen();
    const scrollRef = useRef(null);
    const [showLeftShadow, setShowLeftShadow] = useState(false);
    const [showRightShadow, setShowRightShadow] = useState(false);

    // biome-ignore lint/correctness/useExhaustiveDependencies: only re-run on listed deps
    useEffect(() => {
        const checkForScrollShadows = () => {
            if (scrollRef.current) {
                const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
                // Show left shadow if scrolled to the right
                setShowLeftShadow(scrollLeft > 0);
                // Show right shadow if can scroll more to the right
                setShowRightShadow(scrollLeft + clientWidth < scrollWidth - 5); // 5px buffer
            }
        };

        checkForScrollShadows();

        // Add scroll event listener
        const currentRef = scrollRef.current;
        if (currentRef) {
            currentRef.addEventListener('scroll', checkForScrollShadows);
        }

        // Cleanup
        return () => {
            if (currentRef) {
                currentRef.removeEventListener('scroll', checkForScrollShadows);
            }
        };
    }, [listPolicies]);

    const { list } = query;
    const mobile = screen?.isMobile;
    const tablet = screen?.isTablet;
    const desktop = screen?.isDesktop;

    const sortBy = list.orderByFieldNames?.[0] || 'creationDate';
    const orderByDesc = list.orderByDesc;

    // Get translated column headers
    const referenceNumberText = intl.formatMessage({ id: 'Policies_List.table.referenceNumber' });
    const policyNumberText = intl.formatMessage({ id: 'Policies_List.table.policyNumber' });
    const quoteNumberText = intl.formatMessage({ id: 'Policies_List.table.quoteNumber' });
    const doingBusinessAsText = intl.formatMessage({ id: 'Policies_List.table.doingBusinessAs' });
    const insuredNameText = intl.formatMessage({ id: 'Policies_List.table.insuredName' });
    const productText = intl.formatMessage({ id: 'Policies_List.table.product' });
    const creationDateText = intl.formatMessage({ id: 'Policies_List.table.creationDate' });
    const statusText = intl.formatMessage({ id: 'Policies_List.table.status' });
    const brokerageText = intl.formatMessage({ id: 'Policies_List.table.brokerage' });
    const assignedToText = intl.formatMessage({ id: 'Policies_List.table.assignedTo' });
    const tagsText = intl.formatMessage({ id: 'Policies_List.table.quoteTags' });
    const typeText = intl.formatMessage({ id: 'Policies_List.table.type' });
    const scrollStartText = intl.formatMessage({ id: 'Policies_List.scroll.start' });
    const scrollEndText = intl.formatMessage({ id: 'Policies_List.scroll.end' });

    const scrollToEdge = (direction) => {
        if (!scrollRef.current) return;
        scrollRef.current.scrollTo({
            left: direction === 'left' ? 0 : scrollRef.current.scrollWidth,
            behavior: 'smooth',
        });
    };

    const sortHandler = (columnName) => {
        if (sortBy === columnName) {
            doReduceQueryElement({ code: 'orderByFieldNames', value: [columnName] });
            doReduceQueryElement({ code: 'orderByDesc', value: !orderByDesc });
        } else {
            doReduceQueryElement({ code: 'orderByFieldNames', value: [columnName] });
            doReduceQueryElement({ code: 'orderByDesc', value: true });
        }
        doGetListPolicies();
    };

    const getMinWidth = () => {
        if (mobile) return '500px';
        if (tablet) return '1500px';
        return '2000px';
    };

    return (
        <TableContainer $isDesktop={desktop}>
            <LeftShadow $visible={showLeftShadow} $offset={mobile ? 0 : EDGE_BUTTON_WIDTH} />
            <RightShadow $visible={showRightShadow} $offset={mobile ? 0 : EDGE_BUTTON_WIDTH} />
            {/* Edge scroll buttons are desktop/tablet only — on mobile, swiping is the natural gesture. */}
            {!mobile && (
                <>
                    <ScrollButton
                        type='button'
                        $side='left'
                        disabled={!showLeftShadow}
                        onClick={() => scrollToEdge('left')}
                        aria-label={scrollStartText}
                    >
                        <ScrollChevron $direction='left' $disabled={!showLeftShadow} />
                    </ScrollButton>
                    <ScrollButton
                        type='button'
                        $side='right'
                        disabled={!showRightShadow}
                        onClick={() => scrollToEdge('right')}
                        aria-label={scrollEndText}
                    >
                        <ScrollChevron $direction='right' $disabled={!showRightShadow} />
                    </ScrollButton>
                </>
            )}
            <ScrollableWrapper ref={scrollRef} $inset={mobile ? 0 : EDGE_BUTTON_WIDTH}>
                <HeaderWrapper>
                    <FixedWidthGrid minWidth={getMinWidth()}>
                        <Row>
                            {!mobile && (
                                <Column md={tablet ? 2 : 0.5}>
                                    <Label text={referenceNumberText} textAlign={alignments.left} />
                                </Column>
                            )}
                            {desktop && (
                                <Column md={1}>
                                    <Container
                                        clickEvent={() => sortHandler('policyNumber')}
                                        verticalAlignment={alignments.center}
                                        inline
                                    >
                                        <LabelItem
                                            text={policyNumberText}
                                            textAlign={alignments.left}
                                            right={sizes.xs}
                                            color={
                                                sortBy === 'policyNumber'
                                                    ? cssVar.colorPrimary
                                                    : cssVar.colorStrokeVeryDark
                                            }
                                            bold={sortBy === 'policyNumber'}
                                            clickable
                                        />
                                        <AZSortIcon $isActive={sortBy === 'policyNumber'} isDesc={orderByDesc} />
                                    </Container>
                                </Column>
                            )}
                            {desktop && (
                                <Column md={1}>
                                    <Container
                                        clickEvent={() => sortHandler('quoteNumber')}
                                        verticalAlignment={alignments.center}
                                        inline
                                    >
                                        <LabelItem
                                            text={quoteNumberText}
                                            textAlign={alignments.left}
                                            right={sizes.xs}
                                            color={
                                                sortBy === 'quoteNumber'
                                                    ? cssVar.colorPrimary
                                                    : cssVar.colorStrokeVeryDark
                                            }
                                            bold={sortBy === 'quoteNumber'}
                                            clickable
                                        />
                                        <AZSortIcon $isActive={sortBy === 'quoteNumber'} isDesc={orderByDesc} />
                                    </Container>
                                </Column>
                            )}
                            {mobile ? (
                                <Column xs={5} md={2}>
                                    <Container
                                        clickEvent={() => sortHandler('doingBusinessAs')}
                                        verticalAlignment={alignments.center}
                                        inline
                                    >
                                        <LabelItem
                                            text={doingBusinessAsText}
                                            textAlign={alignments.left}
                                            right={sizes.xs}
                                            color={
                                                sortBy === 'doingBusinessAs'
                                                    ? cssVar.colorPrimary
                                                    : cssVar.colorStrokeVeryDark
                                            }
                                            bold={sortBy === 'doingBusinessAs'}
                                            clickable
                                        />
                                        <AZSortIcon $isActive={sortBy === 'doingBusinessAs'} isDesc={orderByDesc} />
                                    </Container>
                                </Column>
                            ) : (
                                <>
                                    <Column md={tablet ? 2 : 1}>
                                        <Container
                                            clickEvent={() => sortHandler('insuredName')}
                                            verticalAlignment={alignments.center}
                                            inline
                                        >
                                            <LabelItem
                                                text={insuredNameText}
                                                textAlign={alignments.left}
                                                right={sizes.xs}
                                                color={
                                                    sortBy === 'insuredName'
                                                        ? cssVar.colorPrimary
                                                        : cssVar.colorStrokeVeryDark
                                                }
                                                bold={sortBy === 'insuredName'}
                                                clickable
                                            />
                                            <AZSortIcon $isActive={sortBy === 'insuredName'} isDesc={orderByDesc} />
                                        </Container>
                                    </Column>
                                    <Column md={2}>
                                        <Container
                                            clickEvent={() => sortHandler('doingBusinessAs')}
                                            verticalAlignment={alignments.center}
                                            inline
                                        >
                                            <LabelItem
                                                text={doingBusinessAsText}
                                                textAlign={alignments.left}
                                                right={sizes.xs}
                                                color={
                                                    sortBy === 'doingBusinessAs'
                                                        ? cssVar.colorPrimary
                                                        : cssVar.colorStrokeVeryDark
                                                }
                                                bold={sortBy === 'doingBusinessAs'}
                                                clickable
                                            />
                                            <AZSortIcon $isActive={sortBy === 'doingBusinessAs'} isDesc={orderByDesc} />
                                        </Container>
                                    </Column>
                                </>
                            )}
                            {!mobile && (
                                <>
                                    <Column md={tablet ? 2 : 1}>
                                        <Label text={productText} textAlign={alignments.left} />
                                    </Column>
                                    <Column md={1}>
                                        <Container
                                            clickEvent={() => sortHandler('creationDate')}
                                            verticalAlignment={alignments.center}
                                            inline
                                        >
                                            <LabelItem
                                                text={creationDateText}
                                                textAlign={alignments.left}
                                                right={sizes.xs}
                                                color={
                                                    sortBy === 'creationDate'
                                                        ? cssVar.colorPrimary
                                                        : cssVar.colorStrokeVeryDark
                                                }
                                                bold={sortBy === 'creationDate'}
                                                clickable
                                            />
                                            <SortIcon $isActive={sortBy === 'creationDate'} isDesc={orderByDesc} />
                                        </Container>
                                    </Column>
                                </>
                            )}
                            <Column xs={4} md={tablet ? 2 : 1}>
                                <Label text={statusText} textAlign={alignments.left} />
                            </Column>
                            {desktop && (
                                <Column md={1}>
                                    <Label text={brokerageText} textAlign={alignments.left} />
                                </Column>
                            )}
                            {desktop && (
                                <Column md={1}>
                                    <Label text={assignedToText} textAlign={alignments.left} />
                                </Column>
                            )}
                            {desktop && (
                                <Column md={0.5}>
                                    <Container
                                        clickEvent={() => sortHandler('quoteColorTag')}
                                        verticalAlignment={alignments.center}
                                        inline
                                    >
                                        <LabelItem
                                            text={tagsText}
                                            textAlign={alignments.left}
                                            right={sizes.xs}
                                            color={
                                                sortBy === 'quoteColorTag'
                                                    ? cssVar.colorPrimary
                                                    : cssVar.colorStrokeVeryDark
                                            }
                                            bold={sortBy === 'quoteColorTag'}
                                            clickable
                                        />
                                        <AZSortIcon $isActive={sortBy === 'quoteColorTag'} isDesc={orderByDesc} />
                                    </Container>
                                </Column>
                            )}
                            <Column xs={2} md={1} lg={1}>
                                <Label text={typeText} textAlign={alignments.left} />
                            </Column>
                        </Row>
                        <Row noguttertop>
                            <Column>
                                <Separator solid />
                            </Column>
                        </Row>
                    </FixedWidthGrid>
                </HeaderWrapper>
                <FixedWidthGrid minWidth={getMinWidth()}>
                    {Array.isArray(listPolicies) &&
                        listPolicies.map((policy) => (
                            <PolicyListItem
                                key={policy.policyTransactionGuid}
                                policy={policy}
                                history={history}
                                context={context}
                                intl={intl}
                            />
                        ))}
                </FixedWidthGrid>
            </ScrollableWrapper>
        </TableContainer>
    );
};

export default Results;
